import { addDays, addMonths, format, isValid, parseISO } from 'date-fns';

/** Tope de cuotas que genera el modal: evita una lista absurda por un cero de más al tipear. */
export const MAXIMO_CUOTAS = 120;

/**
 * Cada frecuencia calcula el vencimiento de la cuota `k` (0 = la primera) A PARTIR DE LA PRIMERA,
 * sin encadenar la anterior: un plan mensual que arranca el 31/01 da 28/02 y vuelve al 31/03, en
 * vez de arrastrar el 28 para siempre.
 */
export const FRECUENCIAS = {
    SEMANAL: { etiqueta: 'Semanal', vencimiento: (primera, k) => addDays(primera, 7 * k) },
    QUINCENAL: { etiqueta: 'Cada 15 días', vencimiento: (primera, k) => addDays(primera, 15 * k) },
    MENSUAL: { etiqueta: 'Mensual', vencimiento: (primera, k) => addMonths(primera, k) },
    BIMESTRAL: { etiqueta: 'Bimestral', vencimiento: (primera, k) => addMonths(primera, 2 * k) },
    TRIMESTRAL: { etiqueta: 'Trimestral', vencimiento: (primera, k) => addMonths(primera, 3 * k) },
};

const aISO = (fecha) => format(fecha, 'yyyy-MM-dd');

/** Hoy en hora local, como 'yyyy-MM-dd' (el formato del <input type="date"> y de la API). */
export const hoyISO = () => aISO(new Date());

/** Vencimiento (ISO) de la cuota `k` de un plan que arranca en `primerVencimiento`; null si la fecha o la frecuencia no sirven. */
export function vencimientoDeCuota(primerVencimiento, frecuencia, k) {
    const primera = primerVencimiento ? parseISO(primerVencimiento) : null;
    const regla = FRECUENCIAS[frecuencia];
    return primera && isValid(primera) && regla ? aISO(regla.vencimiento(primera, k)) : null;
}

/**
 * Reparte `total` (en pesos enteros) en `cantidad` cuotas iguales. Lo que no divide justo (el resto
 * de la división) lo lleva la ÚLTIMA cuota, así la suma da exactamente el total: 1.000.000 en 3 son
 * 333.333 + 333.333 + 333.334. Devuelve null si el total o la cantidad no alcanzan (una cuota no
 * puede quedar en cero pesos).
 */
export function repartirTotal(total, cantidad) {
    const monto = Math.round(Number(total));
    const partes = Number(cantidad);
    if (!Number.isInteger(partes) || partes < 1 || partes > MAXIMO_CUOTAS) return null;
    if (!(monto > 0)) return null;

    const base = Math.floor(monto / partes);
    if (base < 1) return null;

    const montos = Array(partes).fill(base);
    montos[partes - 1] += monto - base * partes;
    return montos;
}

/**
 * Arma las cuotas pendientes del plan a partir de la configuración (monto a financiar, cantidad,
 * frecuencia y primer vencimiento). Devuelve null si todavía no alcanza (falta el monto o la fecha,
 * o la cantidad no es un entero entre 1 y MAXIMO_CUOTAS): el modal deja entonces las filas como
 * estaban, en vez de vaciarlas mientras el usuario tipea.
 *
 * La fila `i` hereda el `id` (y la observación) de la cuota pendiente `i` que ya existía. El
 * backend edita en el lugar las cuotas que vuelven con `id` (conservan sus pagos imputados) y
 * BORRA las pendientes que no vuelven (ver CobroService.crearOReprogramarPlan): regenerar todo sin
 * `id` haría desaparecer las cuotas viejas junto con lo que tuvieran imputado.
 *
 * `cuotasPendientes` tiene que venir ordenada por número.
 */
export function generarCuotas({ primerVencimiento, cantidad, frecuencia, total }, cuotasPendientes = []) {
    const montos = repartirTotal(total, cantidad);
    if (!montos) return null;
    if (!vencimientoDeCuota(primerVencimiento, frecuencia, 0)) return null;

    return montos.map((monto, i) => ({
        id: cuotasPendientes[i]?.id ?? null,
        monto,
        fechaVencimiento: vencimientoDeCuota(primerVencimiento, frecuencia, i),
        observacion: cuotasPendientes[i]?.observacion || '',
    }));
}

/**
 * Valores iniciales de los campos de configuración del modal.
 *
 * El monto a financiar es el saldo del cobro (`montoBase`, ver montoAFinanciar); si el cobro no
 * tiene monto pactado, la suma de las cuotas pendientes, y si tampoco hay, vacío para que se
 * escriba. Sin cuotas pendientes: hoy, una cuota y mensual. Con plan: primer vencimiento y cantidad
 * salen de las pendientes, y la frecuencia es la que reproduce EXACTAMENTE sus fechas (mensual si
 * el plan es irregular o si con una sola cuota da lo mismo cualquiera).
 *
 * Es solo lo que muestran los campos: la vista previa arranca con las cuotas reales, no las
 * regenera, para no pisar un plan armado a mano (una cuota con otro importe u otra fecha).
 */
export function inferirConfiguracion(cuotasPendientes = [], hoy = hoyISO(), montoBase = null) {
    if (cuotasPendientes.length === 0) {
        return { primerVencimiento: hoy, cantidad: 1, frecuencia: 'MENSUAL', total: montoBase ?? '' };
    }

    const primerVencimiento = cuotasPendientes[0].fechaVencimiento || hoy;
    const reproduceLasFechas = (clave) => cuotasPendientes.every(
        (c, i) => c.fechaVencimiento && c.fechaVencimiento === vencimientoDeCuota(primerVencimiento, clave, i)
    );
    // MENSUAL va primero para que gane los empates.
    const frecuencia = ['MENSUAL', ...Object.keys(FRECUENCIAS)].find(reproduceLasFechas) || 'MENSUAL';

    return {
        primerVencimiento,
        cantidad: cuotasPendientes.length,
        frecuencia,
        total: montoBase ?? totalCuotas(cuotasPendientes),
    };
}

/** Suma de los montos de las filas (una fila sin monto válido cuenta como cero). */
export const totalCuotas = (filas) => filas.reduce((acumulado, f) => acumulado + (Number(f.monto) || 0), 0);

/** Lo ya pagado (imputado) a una cuota. */
export function montoImputado(cuota, pagos = []) {
    return pagos
        .filter((p) => p.idCuota === cuota.id)
        .reduce((suma, p) => suma + Number(p.monto || 0), 0);
}

/** Lo que falta pagar de una cuota (nunca negativo: si se pagó de más, no falta nada). */
export const pendienteDeCuota = (cuota, pagos = []) => Math.max(0, Number(cuota.monto || 0) - montoImputado(cuota, pagos));

/**
 * Cuánto hay que cubrir con las cuotas pendientes del plan: el saldo del cobro (pactado − todo lo
 * pagado, calculado por el backend, que es quien manda) MÁS lo que ya se pagó a cuenta de esas
 * mismas cuotas. Ese pago parcial sigue imputado a su cuota cuando se reprograma, así que su monto
 * tiene que seguir siendo parte del plan; si no, el plan quedaría corto por ese pago.
 *
 * Es null si el cobro no tiene monto pactado (no hay saldo): entonces el monto se escribe a mano.
 */
export function montoAFinanciar(cobro) {
    if (cobro.saldo === null || cobro.saldo === undefined) return null;
    const pendientes = (cobro.cuotas || []).filter((c) => !c.anulada && !c.pagada);
    const yaImputado = pendientes.reduce((suma, c) => suma + montoImputado(c, cobro.pagos || []), 0);
    return Math.max(0, Number(cobro.saldo) + yaImputado);
}
